import { memo, useCallback } from 'react';
import { FundBadgeList } from './FundBadgeList';
import { Badge } from '@/components/ui/badge';
import { investorService } from '@/services/investorService';
import { formatDateTime, formatRelativeTime } from '@/utils/formatters';
import type {
  InvestorDetail,
  InvestorSummary,
  InvestorWithFundContext,
  InvestorContactSummary,
} from '@/types/investor';

type InvestorTableItem =
  InvestorSummary | InvestorDetail | InvestorWithFundContext;

interface InvestorsTableRowProps {
  investor: InvestorTableItem;
  fundId?: string;
  isSelected: boolean;
  onClick: (investor: InvestorTableItem) => void;
  onHover: (investorId: string) => void;
}

interface ContactRowProps {
  investor: InvestorTableItem;
  contact: InvestorContactSummary;
  commitment: string;
  isFirstRow: boolean;
  rowCount: number;
  isSelected: boolean;
  onClick: (investor: InvestorTableItem) => void;
  onHover: (investorId: string) => void;
}

const ContactRow = memo(function ContactRow({
  investor,
  contact,
  commitment,
  isFirstRow,
  rowCount,
  isSelected,
  onClick,
  onHover,
}: ContactRowProps) {
  const handleClick = useCallback(() => {
    onClick(investor);
  }, [onClick, investor]);

  const handleHover = useCallback(() => {
    onHover(investor.id);
  }, [onHover, investor.id]);

  return (
    <tr
      className={`cursor-pointer transition-colors ${
        isSelected ? 'bg-accent border-accent' : 'hover:bg-accent'
      } ${!isFirstRow ? 'border-t-0' : ''}`}
      onClick={handleClick}
      onMouseEnter={handleHover}
    >
      {/* Investor name - only show on first row, span all contact rows */}
      {isFirstRow ? (
        <td
          className="px-6 py-2 whitespace-nowrap align-top"
          rowSpan={rowCount}
        >
          <span className="text-sm font-medium text-foreground">
            {investor.name}
          </span>
          {(investor as InvestorSummary).is_archived && (
            <Badge variant="secondary" className="ml-2">
              Archived
            </Badge>
          )}
        </td>
      ) : null}

      {/* Entity type - only show on first row */}
      {isFirstRow ? (
        <td
          className="px-6 py-2 whitespace-nowrap align-top"
          rowSpan={rowCount}
        >
          <span className="text-sm text-foreground">
            {investorService.getInvestorTypeLabel(investor.investor_type)}
          </span>
        </td>
      ) : null}

      {/* Funds - only show on first row */}
      {isFirstRow ? (
        <td
          className="px-6 py-2 text-sm text-foreground align-top"
          rowSpan={rowCount}
        >
          <FundBadgeList funds={investor.fund_relationships} />
        </td>
      ) : null}

      {/* Total commitment - only show on first row */}
      {isFirstRow ? (
        <td
          className="px-6 py-2 whitespace-nowrap text-sm text-foreground text-right align-top"
          rowSpan={rowCount}
        >
          {commitment}
        </td>
      ) : null}

      {/* Contact name - one per row */}
      <td className="px-6 py-2 whitespace-nowrap">
        <span className="text-sm text-foreground">{contact.name}</span>
      </td>

      {/* Last portal activity - one per contact row */}
      <td className="px-6 py-2 whitespace-nowrap text-sm text-muted-foreground">
        {contact.last_portal_activity && (
          <>
            {formatDateTime(contact.last_portal_activity)}{' '}
            <span className="text-muted-foreground/70">
              ({formatRelativeTime(contact.last_portal_activity)})
            </span>
          </>
        )}
      </td>
    </tr>
  );
});

export const InvestorsTableRow = memo(function InvestorsTableRow({
  investor,
  fundId,
  isSelected,
  onClick,
  onHover,
}: InvestorsTableRowProps) {
  const contacts = (investor as InvestorSummary).contacts || [];
  const rowCount = Math.max(contacts.length, 1);
  const commitment = investorService.formatTotalCommitment({
    fund_relationships: fundId
      ? investor.fund_relationships.filter((rel) => rel.fund_id === fundId)
      : investor.fund_relationships,
  });

  const handleClick = useCallback(() => {
    onClick(investor);
  }, [onClick, investor]);

  const handleHover = useCallback(() => {
    onHover(investor.id);
  }, [onHover, investor.id]);

  // If no contacts, render a single row
  if (contacts.length === 0) {
    return (
      <tr
        className={`cursor-pointer transition-colors ${
          isSelected ? 'bg-accent border-accent' : 'hover:bg-accent'
        }`}
        onClick={handleClick}
        onMouseEnter={handleHover}
      >
        <td className="px-6 py-2 whitespace-nowrap">
          <span className="text-sm font-medium text-foreground">
            {investor.name}
          </span>
          {(investor as InvestorSummary).is_archived && (
            <Badge variant="secondary" className="ml-2">
              Archived
            </Badge>
          )}
        </td>
        <td className="px-6 py-2 whitespace-nowrap">
          <span className="text-sm text-foreground">
            {investorService.getInvestorTypeLabel(investor.investor_type)}
          </span>
        </td>
        <td className="px-6 py-2 text-sm text-foreground">
          <FundBadgeList funds={investor.fund_relationships} />
        </td>
        <td className="px-6 py-2 whitespace-nowrap text-sm text-foreground text-right">
          {commitment}
        </td>
        <td className="px-6 py-2 whitespace-nowrap" />
        <td className="px-6 py-2 whitespace-nowrap text-sm text-muted-foreground" />
      </tr>
    );
  }

  // Render multiple rows for contacts, grouped by investor
  return (
    <>
      {contacts.map((contact: InvestorContactSummary, index: number) => (
        <ContactRow
          key={contact.id}
          investor={investor}
          contact={contact}
          commitment={commitment}
          isFirstRow={index === 0}
          rowCount={rowCount}
          isSelected={isSelected}
          onClick={onClick}
          onHover={onHover}
        />
      ))}
    </>
  );
});
